import {
  Button,
  Dialog,
  Div,
  Flex,
  PricingTile,
  RadioOptionCardProps,
  Separator,
  Spacer,
  SubscriptionUpsaleDialog,
  Text,
  Transition as QDSTransition,
} from '@sendoutcards/quantum-design-ui'
import React, { useContext, useEffect, useState } from 'react'
import connect from 'src/redux/connect'
import { Disclaimer } from 'src/marketing/plans/components'
import { openLaunchPage, openPlans } from 'src/redux/actions/routeAliases'
import { getCardCatalog } from 'src/redux/selectors/catalog'
import { CardCatalog } from 'src/catalog/types'
import Future from 'src/utils/Future'
import CreateAccountForm from 'src/promo/components/CreateAccount'
import {
  AccountFragment,
  AccountInput,
  AddressInput,
  CreateAccount,
  CreateAccountData,
  GetUpsaleDialogMarketingContent,
  GetUpsaleDialogMarketingContentData,
  UpdateAccount,
  UpdateAccountData,
} from 'src/graphql'
import InnerPaymentSheet, {
  UpsaleResponseData,
} from '../components/InnerPaymentSheet'
import { openApp } from 'src/helpers/appHelpers'
import { Transition } from 'src/chrome'
import { PromoContext } from '../context/promoContext'
import { performOperation } from 'src/app/api'
import useActions from 'src/hooks/useActions'
import { PromptingsUpsaleBanner } from '../components/PromptingsUpsaleBanner'

type PromoProps = {
  promo?: string
  sponsorId?: string
  openLaunchPage: () => void
  cardCatalog: Future<CardCatalog>
  isMobile: boolean
}

type ExpandedOptionChild = {
  expandedChild?: React.ReactNode
}

type OptionType = ExpandedOptionChild & RadioOptionCardProps

type Step =
  | 'SelectingPlan'
  | 'CreatingAccount'
  | 'PurchasingPlan'
  | 'PurchaseSuccessful'

const discountDisclaimer = [
  '* Discounts apply to most gifts. Excludes: books, apparel, and business-building tools.',
]

const options: OptionType[] = [
  {
    title: '$97.00/Month',
    subtitle: 'Unlimited Heartfelt Prompting Cards - stamps included!',
    isFeatured: true,
    optionId: '42', // plan ID from Plan table
    isSelected: false,
    expandedChild: (
      <>
        <PricingTile
          features={[
            {
              feature: 'Send Unlimited Heartfelt Prompting Cards Each Month!',
              subFeatures: [
                'Unlimited Postcards (Postage included!)',
                'Unlimited Two-Panel Cards (Postage included!)',
                'A Heartfelt Prompting Card has an immediate send with a unique message to a single recipient.',
              ],
            },
            {
              feature: '30 System Sends each month',
              subFeatures: ['$1.50 per card beyond the included 30 cards.'],
            },
            'Premier discounted shipping on top-selling gifts',
            'Save 30 percent discount on our bestselling gifts*',
            'Premium Digital Storage for pictures and addresses',
            'Postage not included on any System Sends',
          ]}
          title="Unlimited"
          billingInterval="Monthly"
          price="$97"
          priceSubtext="/month"
          hasShadow={false}
        />
        <Div outset={{ horizontal: 'x4' }}>
          <Separator orientation="horizontal" />
          <Spacer space="x2" />
          <Disclaimer messages={discountDisclaimer} />
        </Div>
      </>
    ),
  },
  {
    title: '$37.00/Month',
    subtitle: '20 Heartfelt Prompting Cards - stamps included!',
    isFeatured: false,
    optionId: '324132', // 6!
    isSelected: false,
    expandedChild: (
      <>
        <PricingTile
          features={[
            {
              feature: 'Send 30 Postcards & Two-Panel Cards Each Month!',
              subFeatures: [
                'Includes both Heartfelt and System Sends.',
                '$1.75 per card beyond the included 30 cards.',
              ],
            },
            'Save 20 percent on our bestselling gifts*',
            'Enhanced Digital Storage for pictures and addresses',
            'Postage not included on any card sends',
          ]}
          title="Card a Day"
          billingInterval="Monthly"
          price="$37"
          priceSubtext="/month"
          hasShadow={false}
        />
        <Div outset={{ horizontal: 'x4' }}>
          <Separator orientation="horizontal" />
          <Spacer space="x2" />
          <Disclaimer messages={discountDisclaimer} />
        </Div>
      </>
    ),
  },
]

const getPlan = (planId: string) => {
  return options.find(option => option.optionId === planId)
}

const Promo: React.FC<PromoProps> = props => {
  const { sponsorId, openLaunchPage, cardCatalog, isMobile } = props
  // Local Component State
  const [isRedirecting, setIsRedirecting] = useState(false)
  const [upsaleResponseData, setUpsaleResponseData] = useState<
    UpsaleResponseData | undefined
  >(undefined)
  const [isSaving, setIsSaving] = useState(false)
  const [selectedPlan, setSelectedPlan] = useState('')
  const [step, setStep] = useState<Step>('SelectingPlan')
  const [showSuccessDialog, setShowSuccessDialog] = useState(false)
  const [upsaleCopy, setUpsaleCopy] = useState({ title: '', description: '' })

  // Context State
  const { state, dispatch } = useContext(PromoContext)
  const {
    account,
    create: {
      form,
      isCheckingUsername,
      isUsernameTaken,
      isCheckingEmail,
      emailValidation: { isEmailTaken },
    },
    payment: { form: paymentForm, shouldHaveExpenseBucket, expenseReload },
  } = state

  performOperation<GetUpsaleDialogMarketingContentData>(
    GetUpsaleDialogMarketingContent(),
    result => {
      if (result.error || !result.value) {
        setUpsaleCopy({
          title: 'Simple Transparent Pricing',
          description: 'No Contracts. No Surprise Fees.',
        })
      } else {
        setUpsaleCopy({
          title: result.value.upsaleDialogContent.title,
          description: result.value.upsaleDialogContent.content,
        })
      }
    },
  )

  const planId = selectedPlan === '324132' ? '6' : '42'
  const actions = useActions()

  type EventNameType = 'postcard_promo_subscription' | 'postcard_promo_account'

  const sendToCIO = (
    eventName: EventNameType,
    account: { id: string; email: string },
  ) => {
    const cioIdentify = async () =>
      window._cio.identify({
        id: account.id,
        email: account.email,
      })

    cioIdentify().then(() => window._cio.track(eventName))
  }

  const handleSelectedOption = (optionId: string) => {
    setSelectedPlan(getPlan(optionId)?.optionId ?? '42')
  }

  const handleClose = () => {
    setIsRedirecting(false)
    openLaunchPage()
  }

  useEffect(() => {
    actions.clearToken()
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    setTimeout(() => {
      if (showSuccessDialog) {
        setShowSuccessDialog(false)
      }
    }, 650)
  }, [showSuccessDialog])

  const onSubmitCreateAccountForm = () => {
    if (isCheckingUsername || isCheckingEmail) return

    setIsSaving(true)

    const userInfoUpdate: AccountInput = {
      ...form,
      firstName: '',
      lastName: '',
      phoneNumber: '',
      sponsorGenealogyId: sponsorId,
    }

    performOperation<CreateAccountData>(
      CreateAccount({ account: userInfoUpdate }),
      result => {
        if (result.value) {
          dispatch({
            type: 'SET_ACCOUNT',
            payload: { account: result.value.createAccount.account },
          })
          actions.saveToken(result.value.createAccount.account.token)
          onAccountUpdated(result.value.createAccount.account)
          setStep('PurchasingPlan')
        }
        setIsSaving(false)
        setShowSuccessDialog(true)
      },
    )
  }

  if (isRedirecting || !cardCatalog.value) {
    return (
      <Transition
        message="Loading..."
        messageStyle={{ color: 'black', fontWeight: 500 }}
      />
    )
  }

  const onAccountUpdated = (updatedAccount: AccountFragment) => {
    dispatch({
      type: 'SET_ACCOUNT',
      payload: { account: updatedAccount },
    })
  }

  const updateUpsaleResponseData = (
    upsaleResponseData: UpsaleResponseData | undefined,
  ) => {
    setUpsaleResponseData(upsaleResponseData)
  }

  const setIsUpdatingAccount = (isUpdatingAccount: boolean) => {
    dispatch({
      type: 'SET_IS_UPDATING_ACCOUNT',
      payload: { isUpdatingAccount },
    })
  }
  // Plan upgrade is successfull, lets open the user account on soc-front
  const onPurchaseSuccessful = (accessToken: string) => {
    sendToCIO('postcard_promo_subscription', {
      id: account?.id ?? '',
      email: account?.email ?? '',
    })
    openApp('', { access_token: accessToken })
    setStep('PurchaseSuccessful')
  }
  const updateAccount = async (account: AccountInput) => {
    performOperation<UpdateAccountData>(
      UpdateAccount({ account: account }),
      result => {
        if (result.value) {
          onAccountUpdated(result.value.updateAccount.account)
          onPurchaseSuccessful(result.value.updateAccount.account.token)
          updateUpsaleResponseData({
            title: 'Your selection was made successfully',
            subTitle: 'Thank you for your subscription',
            isLoading: false,
            loadedStatus: 'success',
          })
        } else {
          updateUpsaleResponseData({
            title: 'An error occurred',
            subTitle:
              'Account failed to update. Contact Support for assistance.',
            isLoading: false,
            loadedStatus: 'error',
          })
        }
        setIsUpdatingAccount(false)
      },
    )
  }

  const onPurchasePlan = () => {
    const firstLastNameData =
      !!account?.firstName && !!account?.lastName
        ? null
        : {
            firstName: paymentForm.firstName,
            lastName: paymentForm.lastName,
          }

    const shippingAddressData =
      account?.shippingAddress && account?.shippingAddress.address1
        ? undefined
        : (paymentForm.shippingAddress as AddressInput)

    const expenseBucket = shouldHaveExpenseBucket
      ? {
          ...expenseReload,
          threshold: expenseReload.threshold / 100,
          isEnabled: true,
        }
      : undefined

    const accountData: AccountInput = {
      plan: planId,
      shippingAddress: shippingAddressData,
      expenseReload: expenseBucket,
      ...(firstLastNameData ?? {}),
    }

    setIsUpdatingAccount(true)

    updateUpsaleResponseData({
      title: 'Making your selection',
      subTitle: 'Thank you for your selection!',
      isLoading: true,
      loadedStatus: undefined,
    })

    updateAccount(accountData)
  }

  const isTrue = true

  const getSubmitDetails = () => {
    const { shippingAddress } = paymentForm
    switch (step) {
      case 'CreatingAccount':
        return {
          title: isSaving ? 'Creating Account' : 'Create Account',
          onClick: onSubmitCreateAccountForm,
          isDisabled:
            isSaving ||
            !form.username ||
            !form.password ||
            !form.email ||
            isCheckingUsername ||
            isUsernameTaken ||
            isEmailTaken ||
            isCheckingEmail,
        }
      case 'PurchasingPlan':
        return {
          title: 'Purchase',
          onClick: onPurchasePlan,
          isDisabled:
            !paymentForm.firstName ||
            !paymentForm.lastName ||
            !shippingAddress ||
            !shippingAddress.address1 ||
            !shippingAddress.city ||
            !shippingAddress.state ||
            !shippingAddress.postalCode ||
            !shippingAddress.country ||
            !account?.stripeSource,
        }
      case 'SelectingPlan':
        return undefined
      default:
        return {
          title: 'Purchasing',
          onClick: () => {
            if (!account) {
              setStep('CreatingAccount')
            } else {
              setStep('PurchasingPlan')
            }
          },
          isDisabled: false,
        }
    }
  }
  return (
    <>
      <Dialog isOpen={showSuccessDialog}>
        <Text type="largeBody">Account created successfully!</Text>
      </Dialog>
      {isTrue && (
        <SubscriptionUpsaleDialog
          title={upsaleCopy.title}
          description={upsaleCopy.description}
          insetOverride="x0"
          maxWidth="calc(100% - 150px)"
          maxHeight="calc(100% - 150px)"
          optionFormLocation="right"
          setIsDialogOpen={() => handleClose()}
          isOpen={true}
          selectableOptions={options}
          selectedOption={selectedPlan}
          handleSelectedOption={handleSelectedOption}
          submitAction={getSubmitDetails()}
          removeSelectedOption={() => {
            setSelectedPlan('')
            setStep('SelectingPlan')
          }}
          acceptSelectedAction={{
            title: 'Continue to Checkout',
            onClick: () => {
              if (!account) {
                setStep('CreatingAccount')
              } else {
                setStep('PurchasingPlan')
              }
            },
          }}
          upsaleContentTitle="Subscription Includes"
          checkoutTitle="Great Choice!"
          checkoutDescription="We are glad you found the plan that works best for you. Please confirm below."
          upsaleContent={<Div />}
          sponsorId={sponsorId ? sponsorId : '9070'}
          upsaleBanner={
            <Div
              alignItems="center"
              flexDirection="column"
              justifyContent="center"
              width="100%"
              outset={{ top: 'x4' }}
            >
              <Text
                type="body"
                weight="bold"
                alignment="center"
                color="primaryHeading"
              >
                OR BUNDLE AND SAVE WITH
              </Text>
              <Spacer space="x2" />
              <PromptingsUpsaleBanner
                onClick={() =>
                  window.open(
                    `https://shop.bydesign.com/SendOutCards/#/shop/detail/15186/from/${sponsorId}?MarketShow=550`,
                  )
                }
              />
            </Div>
          }
          successComponent={
            typeof upsaleResponseData !== undefined && upsaleResponseData ? (
              <Flex
                alignItems={'center'}
                justifyContent={'center'}
                width={'100%'}
                flexDirection={'column'}
                height={'100%'}
              >
                <Div width={'100%'}>
                  <QDSTransition
                    title={upsaleResponseData.title}
                    isLoading={upsaleResponseData.isLoading}
                    subtitle={upsaleResponseData.subTitle}
                    loadedStatus={upsaleResponseData.loadedStatus}
                  />
                </Div>
                {upsaleResponseData.loadedStatus === 'error' && (
                  <Div outset={{ top: 'auto' }} width={'100%'}>
                    <Button
                      fullWidth={true}
                      title={'Go back'}
                      onClick={() => setUpsaleResponseData(undefined)}
                    />
                  </Div>
                )}
              </Flex>
            ) : (
              undefined
            )
          }
        >
          {step === 'CreatingAccount' && (
            <CreateAccountForm
              isMobile={isMobile}
              isCreatingAccount={isSaving}
            />
          )}
          {step === 'PurchasingPlan' && account && (
            <InnerPaymentSheet
              planId={planId}
              onAccountUpdated={onAccountUpdated}
            />
          )}
        </SubscriptionUpsaleDialog>
      )}
    </>
  )
}

export default connect(
  state => {
    return {
      promo: state.promo.promo,
      cardCatalog: getCardCatalog(state),
      sponsorId: state.storefront.sponsorSlug
        ? state.storefront.sponsorSlug
        : state.storefront.storefront?.user.genealogyId.toString(),
      isMobile: state.window.isMobile,
    }
  },
  { openPlans, openLaunchPage },
)(Promo)
